import AgTechGrid from '@agtech/web/components/Grids/AgTechGrid';
import AgTechGridLink from '@agtech/web/components/Grids/Controls/AgTechGridLink';
import { AgTechGridProps } from '@agtech/web/components/Grids/Data/AgTechGridProps';
import { ChargeDiscount, SaleInvoiceModifier } from '@sales/data/sales/InvoiceModifiers/SaleInvoiceModifierData';
import React from 'react'
import SaleInvoiceModifierDialog from '../../dialogs/SaleInvoiceModifierDialog';
import { SaleInvoiceModifierActionButton } from '../buttons/SaleInvoiceModifierButtons';
import { AgTechIndex } from '@agtech/core/utilities/AgTechUtilities';
import { useAgTechOptionalQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import { InvoiceModifierDataGetAllInvoiceModifiersQuery } from '@sales/data/sales/InvoiceModifiers/SaleInvoiceModifierDataQueries';
import AgTechHelpModal from '@agtech/web/components/Portals/Modals/AgTechHelpModal';

const SaleInvoiceModifierViewGrid = (props: {
    InvoiceModifiers?: AgTechIndex<SaleInvoiceModifier>,
    gridProps?: (props: AgTechGridProps<SaleInvoiceModifier>) => AgTechGridProps<SaleInvoiceModifier>
}) => {
    const gridQuery = useAgTechOptionalQuery({
        query: InvoiceModifierDataGetAllInvoiceModifiersQuery,
        data: props.InvoiceModifiers,
        projection: (index) => {
            return {
                items: Object.values(index)
            };
        }
    });

    return (
        <AgTechGrid
            header={{
                title: `Charges and discounts`,
                content: () => (
                    <>
                        <div id='settings-modifier-create'>
                            <SaleInvoiceModifierDialog.Button />
                        </div>
                        <AgTechHelpModal.Button
                            title='Charges and Discounts'
                            content={
                                <div className='row-based gap-y-3 flex'>
                                    <p>Charges and discounts are modifiers applied automatically to invoices during invoicing. Use them to add fees (e.g. a buyer's premium) or deductions (e.g. a loyalty discount) across all sales.</p>
                                    <p>Each modifier has a <strong>name</strong>, an <strong>amount</strong> (fixed dollar or percentage), and a <strong>category</strong> of either Charge or Discount. Click any row to edit an existing modifier.</p>
                                </div>
                            }
                        />
                    </>
                )
            }}
            data={{
                itemQuery: gridQuery
            }}
            columns={[
                {
                    header: "Name",
                    classes: 'bg-slate-100 emphasized',
                    size: 2,
                    value: InvoiceModifier => (
                        <AgTechGridLink
                            content={InvoiceModifier.name}
                            onClick={e => e.dialogs.openDialog({
                                dialog: () => <SaleInvoiceModifierDialog InvoiceModifier={InvoiceModifier} />
                            })}
                        />
                    ),
                },
                {
                    header: "Amount",
                    value: InvoiceModifier => (InvoiceModifier.isPercentage ? '' : '$') + InvoiceModifier.amount + (InvoiceModifier.isPercentage ? '%' : '')
                },
                {
                    header: "Type",
                    value: InvoiceModifier => InvoiceModifier.isPercentage ? 'Percentage' : 'Amount'
                },
                {
                    header: "Category",
                    value: InvoiceModifier => InvoiceModifier.modifierType === ChargeDiscount.Discount ? "Discount" : "Charge"
                }
            ]}
            isRowEntityTheSame={(prev, curr) => {
                return prev.name === curr.name &&
                    prev.isPercentage === curr.isPercentage &&
                    prev.amount === curr.amount &&
                    prev.modifierType === curr.modifierType;
            }}
            noRecordsConfig={{
                message: 'No charges or discounts were found in the system',
                action: {
                    prompt: 'To create one, click the button below',
                    button: () => <SaleInvoiceModifierDialog.Button classes='button-highlight' />
                }
            }}
            actions={{
                rendering: c => <SaleInvoiceModifierActionButton InvoiceModifier={c} />
            }}
            propModifier={props.gridProps}
        />
    );
}

export default SaleInvoiceModifierViewGrid